'use client'

import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '@/lib/utils'

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  )
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const [activeTabBounds, setActiveTabBounds] = React.useState<{
    width: number
    left: number
  } | null>(null)

  React.useEffect(() => {
    const updateActiveTab = () => {
      const activeTab = document.querySelector(
        '[data-slot="tabs-trigger"][data-state="active"]',
      )
      if (activeTab) {
        const bounds = activeTab.getBoundingClientRect()
        const parentBounds = activeTab.parentElement!.getBoundingClientRect()
        setActiveTabBounds({
          width: bounds.width,
          left: bounds.left - parentBounds.left,
        })
      }
    }

    // Initial measurement
    updateActiveTab()

    // Update on resize
    window.addEventListener('resize', updateActiveTab)

    // Use MutationObserver to detect data-state changes
    const observer = new MutationObserver(updateActiveTab)
    const tabsList = document.querySelector('[data-slot="tabs-list"]')
    if (tabsList) {
      observer.observe(tabsList, {
        attributes: true,
        subtree: true,
        attributeFilter: ['data-state'],
      })
    }

    return () => {
      window.removeEventListener('resize', updateActiveTab)
      observer.disconnect()
    }
  }, [])

  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        'bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px] gap-1 relative',
        className,
      )}
      {...props}
    >
      {/* Animated background pill */}
      {activeTabBounds && (
        <div
          className="absolute bg-background shadow-sm rounded-md transition-all duration-300 ease-in-out"
          style={{
            width: activeTabBounds.width,
            height: 'calc(100% - 6px)',
            left: activeTabBounds.left,
            top: '3px',
          }}
        />
      )}
      {props.children}
    </TabsPrimitive.List>
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring text-gray-500 dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-colors duration-300 ease-in-out focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-foreground relative [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
